import { reactiveComputed } from '@vueuse/core';
import { readonly, type Ref } from 'vue';

import {
    BackendErrorCode,
    type BackendErrorEntity,
    type ErrorType,
    type NegotiationPlatformEntity,
    useApiAcceptNegotiation,
    useApiBuyerStopNegotiationProposals,
    useApiMakeNegotiationProposal,
    useApiSellerDeclineNegotiation,
} from '~/apiClient';
import { NotificationDuration } from '~/types/notifications.type';
import type { AuctionItemEntityWithNegotiation } from '~/types/sale.type';

type UseAuctionNegotiationReturn = {
    negotiation: ComputedRef<NegotiationPlatformEntity>;
    isBuyer: Readonly<Ref<boolean>>;
    isEnded: Readonly<Ref<boolean>>;
    isAccepted: Readonly<Ref<boolean>>;
    isExpired: Readonly<Ref<boolean>>;
    isUnderSellerReview: Readonly<Ref<boolean>>;
    counterPartProposal: ComputedRef<number>;

    buyerStopProposals: () => Promise<NegotiationPlatformEntity>;
    buyerStopProposalsIsPending: Ref<boolean>;
    makeProposal: (amount: number) => Promise<NegotiationPlatformEntity>;
    makeProposalIsPending: Ref<boolean>;
    accept: () => Promise<NegotiationPlatformEntity>;
    acceptIsPending: Ref<boolean>;
    sellerDecline: () => Promise<NegotiationPlatformEntity>;
    sellerDeclineIsPending: Ref<boolean>;
};

export default function useAuctionNegotiation(auction: MaybeRefOrGetter<AuctionItemEntityWithNegotiation>): UseAuctionNegotiationReturn {
    // COMPOSABLES
    const { tt, tn } = useTypedI18n();
    const { notifySuccess, notifyInfo, notifyError } = useNotification();
    const { logError } = useLogs();
    const { data: authData } = useAuthUtils();
    const { extendNegotiation } = useExtendedNegotiation();
    const { mutateAsync: buyerStopProposalsMutateAsync, isPending: buyerStopProposalsIsPending } = useApiBuyerStopNegotiationProposals();
    const { mutateAsync: makeProposalMutateAsync, isPending: makeProposalIsPending } = useApiMakeNegotiationProposal();
    const { mutateAsync: acceptMutateAsync, isPending: acceptIsPending } = useApiAcceptNegotiation();
    const { mutateAsync: sellerDeclineMutateAsync, isPending: sellerDeclineIsPending } = useApiSellerDeclineNegotiation();

    // DATA
    const negotiation = computed(() => toValue(auction).negotiation);
    const extendedNegotiation = readonly(reactiveComputed(() => extendNegotiation(negotiation.value)));
    const { isBuyer, isEnded, isAccepted, isExpired, isUnderSellerReview } = toRefs(extendedNegotiation);

    const counterPartProposal = computed(() =>
        isBuyer.value ? negotiation.value.currentSellerProposal.amount : negotiation.value.currentBuyerProposal.amount
    );

    // METHODS
    const notifyGenericApiError = (apiError: ErrorType<BackendErrorEntity>): never => {
        if (
            isBackendError(apiError.data) &&
            [BackendErrorCode.AuthTokenExpired, BackendErrorCode.AuthTokenNotValid].includes(apiError.data.error.code)
        ) {
            // Errors already handled elsewhere
        } else {
            notifyError({
                title: tt('auctionNegotiation.notifications.apiError.title'),
                description: tt('auctionNegotiation.notifications.apiError.description'),
                duration: NotificationDuration.NoTimeout,
            });
        }

        throw apiError;
    };

    const makeLogErrorHandler =
        (action: string) =>
        (error: ErrorType<BackendErrorEntity>, variables: Object): void => {
            logError('Error in Auction Negotiation', {
                action,
                auctionId: toValue(auction).id,
                userId: authData.value?.user?.id,
                apiError: error.data,
                apiData: variables,
            });
        };

    const buyerStopProposals: UseAuctionNegotiationReturn['buyerStopProposals'] = () =>
        buyerStopProposalsMutateAsync({ negotiationId: negotiation.value.id }, { onError: makeLogErrorHandler('buyerStopProposals') })
            .then(updatedNegotiation => {
                notifyInfo({
                    title: tt('auctionNegotiation.notifications.stopProposalsByBuyer.title'),
                    description: tt('auctionNegotiation.notifications.stopProposalsByBuyer.description', {
                        amount: tn(updatedNegotiation.currentBuyerProposal.amount, 'currency'),
                    }),
                });

                return updatedNegotiation;
            })
            .catch(notifyGenericApiError);

    const makeProposal: UseAuctionNegotiationReturn['makeProposal'] = amount =>
        makeProposalMutateAsync({ negotiationId: negotiation.value.id, data: { amount } }, { onError: makeLogErrorHandler('makeProposal') })
            .then(updatedNegotiation => {
                if (updatedNegotiation.outcome && updatedNegotiation.closeAmount === amount) {
                    notifySuccess({
                        title: tt(
                            isBuyer.value
                                ? 'auctionNegotiation.notifications.acceptByBuyer.title'
                                : 'auctionNegotiation.notifications.acceptBySeller.title'
                        ),
                        description: tt(
                            isBuyer.value
                                ? 'auctionNegotiation.notifications.acceptByBuyer.description'
                                : 'auctionNegotiation.notifications.acceptBySeller.description',
                            { amount: tn(amount, 'currency') }
                        ),
                    });
                } else {
                    notifyInfo({
                        title: tt(
                            isBuyer.value
                                ? 'auctionNegotiation.notifications.proposalByBuyer.title'
                                : 'auctionNegotiation.notifications.proposalBySeller.title'
                        ),
                        description: tt(
                            isBuyer.value
                                ? 'auctionNegotiation.notifications.proposalByBuyer.description'
                                : 'auctionNegotiation.notifications.proposalBySeller.description',
                            { amount: tn(amount, 'currency') }
                        ),
                    });
                }

                return updatedNegotiation;
            })
            .catch(notifyGenericApiError);

    const accept: UseAuctionNegotiationReturn['accept'] = () =>
        acceptMutateAsync({ negotiationId: negotiation.value.id }, { onError: makeLogErrorHandler('accept') })
            .then(updatedNegotiation => {
                notifySuccess({
                    title: tt(
                        isBuyer.value
                            ? 'auctionNegotiation.notifications.acceptByBuyer.title'
                            : 'auctionNegotiation.notifications.acceptBySeller.title'
                    ),
                    description: tt(
                        isBuyer.value
                            ? 'auctionNegotiation.notifications.acceptByBuyer.description'
                            : 'auctionNegotiation.notifications.acceptBySeller.description',
                        { amount: tn(updatedNegotiation.closeAmount ?? counterPartProposal.value, 'currency') }
                    ),
                });

                return updatedNegotiation;
            })
            .catch(notifyGenericApiError);

    const sellerDecline: UseAuctionNegotiationReturn['sellerDecline'] = () =>
        sellerDeclineMutateAsync({ negotiationId: negotiation.value.id }, { onError: makeLogErrorHandler('sellerDecline') })
            .then(updatedNegotiation => {
                notifyInfo({
                    title: tt('auctionNegotiation.notifications.declinedBySeller.title'),
                    description: tt('auctionNegotiation.notifications.declinedBySeller.description', {
                        amount: tn(negotiation.value.currentBuyerProposal.amount, 'currency'),
                    }),
                });

                return updatedNegotiation;
            })
            .catch(notifyGenericApiError);

    return {
        negotiation,
        isBuyer,
        isEnded,
        isAccepted,
        isExpired,
        isUnderSellerReview,
        counterPartProposal,

        buyerStopProposals,
        buyerStopProposalsIsPending,
        makeProposal,
        makeProposalIsPending,
        accept,
        acceptIsPending,
        sellerDecline,
        sellerDeclineIsPending,
    };
}
